import { useAppsInstalledApi } from '@hero/api-hooks';
import type { AppsInstalledHookAPI } from '@hero/api-hooks';
import type { AppFilingMode } from '@hero/schema';

export interface LogistiFiling {
  /** A row exists and is enabled — the connected badge, the settings screen. */
  isConnected: boolean;
  /**
   * A document can actually be filed right now.
   *
   * Stricter than `isConnected`: a connected row whose identity is missing (a
   * platform tenant with no CR yet, a driver with no issue number) cannot
   * produce a وثيقة, and offering the action would spend a user's attention on
   * a button that fails.
   */
  canIssue: boolean;
  /** Whose licence files. `undefined` when there is no row at all. */
  filingMode?: AppFilingMode;
}

/**
 * How the signed-in tenant files government waybills (Bayan), if at all.
 *
 * One definition for both platforms and for all three filing modes. Every
 * surface that used to ask this question its own way now reads it here: the
 * order action, the goods-value requirement on the load form, the integrations
 * card.
 *
 * ## Why the install list and not `bayan_integration`
 *
 * The flag decides whether Hero *offers* the connector during rollout. A tenant
 * already filing real documents with real Saudi authorities needs Bayan-valid
 * load data regardless of what the flag says today; gating the data
 * requirements on a rollout flag would leave that tenant quietly issuing
 * rejected waybills the moment the flag went off.
 *
 * ## Why `canIssue` comes from the server
 *
 * It used to have its own endpoint (`GET /apps/logisti/capability`), which
 * existed only because platform filing wrote no install row for this list to
 * read. Now that every mode writes one, the answer rides along on a query the
 * app already has cached for the session — one request fewer per order screen —
 * and the rule that derives it stays in one place on the backend rather than
 * being re-implemented per surface.
 */
export const useLogistiFiling = (apiConfig?: AppsInstalledHookAPI): LogistiFiling => {
  const { data: installedApps } = useAppsInstalledApi(apiConfig);
  const install = installedApps?.find(app => app.appId === 'logisti');

  return {
    isConnected: Boolean(install?.enabled),
    canIssue: Boolean(install?.canIssue),
    filingMode: install?.filingMode,
  };
};
